import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CronFlavor, CronOptions} from '@zapinfo/ngx-cron-editor';
import { timeZoneNames } from 'app/helpers/timezone-options';
import { formGroupPatchValueNullFriendly } from 'app/helpers/utils';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { ReportAutoUpload } from 'app/models/report-auto-upload';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { ReportService } from 'app/services/report.service';
import { TeamService } from 'app/services/team.service';
import { UserService } from 'app/services/user.service';
import { ToastService } from 'ng-uikit-pro-standard';
import { finalize } from 'rxjs/operators';
import { AddEditReportAutoUploadComponent } from '../add-edit-report-auto-upload/add-edit-report-auto-upload.component';

@Component({
  selector: 'app-add-edit-report',
  templateUrl: './add-edit-report.component.html',
  styleUrls: ['./add-edit-report.component.scss']
})
export class AddEditReportComponent implements OnInit {

  constructor(public activatedRoute: ActivatedRoute, private ecoDocsTitleService: EcoDocsTitleService,
              public reportService: ReportService, public userService: UserService, public teamService: TeamService,
              private validationHandler: ValidationHandler, private router: Router, private toastService: ToastService) { }

  isLoading: boolean = false;
  addEditReportFormGroup: FormGroup = null;

  editRuleIndex: number = -1;
  
  autoUploads: ReportAutoUpload[];
  cronExpression: string = '* * * * *';

  @ViewChild('addEditSaveRule', { static: true }) addEditSaveRule: AddEditReportAutoUploadComponent;
  @ViewChild('cronEditor') cronEditor: any;
  
  public cronOptions: CronOptions = {
    formInputClass: 'cron-editor-input',
    formSelectClass: 'cron-editor-select',
    formRadioClass: 'cron-editor-radio',
    formCheckboxClass: 'cron-editor-checkbox',
    
    defaultTime: "00:00:00",

    hideMinutesTab: true,
    hideHourlyTab: false,
    hideDailyTab: false,
    hideWeeklyTab: false,
    hideMonthlyTab: false,
    hideYearlyTab: false,
    hideAdvancedTab: true,
    hideSpecificWeekDayTab : false,
    hideSpecificMonthWeekTab : false,

    use24HourTime: true,
    hideSeconds: true,

    cronFlavor: CronFlavor.standard
  };
  
  ngOnInit(): void {

    this.addEditReportFormGroup = this.reportService.formGroup();
    this.cronExpression = this.addEditReportFormGroup.value.expression;

    if (this.activatedRoute.snapshot.params.reportid != 'new') {
      this.ecoDocsTitleService.setTitle( 'Edit Report', false );

      this.isLoading = true;
      this.reportService.getById(this.activatedRoute.snapshot.params.reportid).pipe(finalize(() => {
        this.isLoading = false;
      })).subscribe(r => {
        this.addEditReportFormGroup = this.reportService.formGroup(r);
        this.cronExpression = this.addEditReportFormGroup.value.expression;
        // hack to force cron editor to update
        this.cronEditor.handleModelChange(this.cronExpression);
        this.setAutoUploadsList();
        this.addEditReportFormGroup.valueChanges.subscribe(() => {
          this.setAutoUploadsList();      
        });
      });      
    } else {
      this.ecoDocsTitleService.setTitle( 'New Report', false );
      this.setAutoUploadsList();
      this.addEditReportFormGroup.valueChanges.subscribe(() => {
        this.setAutoUploadsList();      
      });
    }

  }

  timeZoneOptions() : string[] {
    return timeZoneNames;
  }

  innerWidth() {
    return window.innerWidth;
  }

  setAutoUploadsList() {
    this.autoUploads = this.addEditReportFormGroup.value.auto_uploads;
  }

  onAddNewRule() {
    this.editRuleIndex = -1;
    let frmGroup: FormGroup = this.reportService.getReportAutoUploadFormGroup({} as ReportAutoUpload);
    this.addEditSaveRule.show(frmGroup);
  }

  onCronChange(newValue: string) {
    this.addEditReportFormGroup.get('expression').setValue(newValue);
  }
  editAutoSaveRule(autoUpload: ReportAutoUpload, index: number) {
    this.editRuleIndex = index;
    let frmGroup: FormGroup = this.reportService.getReportAutoUploadFormGroup(autoUpload);
    formGroupPatchValueNullFriendly(frmGroup, autoUpload);
    this.addEditSaveRule.show(frmGroup);
  }

  deleteAutoSaveRule(autoUpload: ReportAutoUpload, index: number){
    let autoUploadsFormArray: FormArray = (this.addEditReportFormGroup.controls['auto_uploads'] as FormArray);
    autoUploadsFormArray.removeAt(index);
  }

  onReportAutoUploadAddEditFinished(frm: FormGroup) {
    let autoUploadsFormArray: FormArray = (this.addEditReportFormGroup.controls['auto_uploads'] as FormArray);

    if (this.editRuleIndex != -1) {
      formGroupPatchValueNullFriendly(autoUploadsFormArray.at(this.editRuleIndex) as FormGroup, frm.value);
      this.editRuleIndex = -1;
    } else {
      autoUploadsFormArray.push(frm);
      frm.setParent(autoUploadsFormArray);
    }
  }

  onFinishedAddEditReport() {
    this.validationHandler.onSubmitCheck(this.addEditReportFormGroup);
    if (!this.addEditReportFormGroup.valid) {
       return;
    }

    this.isLoading = true;
    this.reportService.put(this.addEditReportFormGroup.value).pipe(finalize(()=>{
      this.isLoading = false;
    })).subscribe(() => {
      this.router.navigate(['/manage/reports']);
    }, () => {
      this.toastService.error('Error saving report!');
    });
  }
}
